import { TemplatesService } from '@beeboard/common';
import { defineStore } from 'pinia';
import { useTemplateStore } from '@/store/templateStore';
import { usePhaseTemplateRulesStore } from './phaseTemplateRulesStore';
import type {
  PhaseTemplate,
  PhaseTemplateUpdate,
  PhaseTemplateSplitRequest,
} from '@beeboard/common';

export const usePhaseTemplateStore = defineStore('phaseTemplateStore', {
  getters: {
    phases() {
      return function (templateId: string): PhaseTemplate[] {
        const templateStore = useTemplateStore();
        const template = templateStore.template(templateId);

        return template.phases;
      };
    },

    phase() {
      return (templateId: string, phaseId: string): PhaseTemplate => {
        const phases = this.phases(templateId);
        const phase = phases.find((phase) => phase.id === phaseId);
        if (!phase) {
          throw new Error('phase not found');
        }

        return phase;
      };
    },
  },

  actions: {
    write(templateId: string, phase: PhaseTemplate): PhaseTemplate {
      const phases = this.phases(templateId);
      const oldPhase = phases.find((pha) => pha.id === phase.id);
      if (oldPhase) {
        Object.assign(oldPhase, phase);
      } else {
        phases.push(phase);
      }
      useTemplateStore().writePhase(templateId, phase);

      return this.phase(templateId, phase.id);
    },

    async add(
      templateId: string,
      payload: PhaseTemplateUpdate,
      before?: string,
    ) {
      const errors = usePhaseTemplateRulesStore().checkAddPhase(
        templateId,
        payload,
        before,
      );
      if (errors) {
        throw errors;
      }
      const { phase } = await TemplatesService.addPhase(
        templateId,
        payload,
        before,
      );
      if (before) {
        const phases = this.phases(templateId);
        const beforeIndex = phases.findIndex(({ id }) => id === before);
        phases.splice(beforeIndex, 0, phase);

        return this.phase(templateId, phase.id);
      } else {
        this.phases(templateId).push(phase);

        return this.phase(templateId, phase.id);
      }
    },

    async update(
      templateId: string,
      phaseId: string,
      payload: PhaseTemplateUpdate,
    ) {
      const errors = usePhaseTemplateRulesStore().checkUpdatePhase(
        templateId,
        phaseId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.updatePhase(templateId, phaseId, payload);
      await useTemplateStore().loadTemplate(templateId);
    },

    async split(
      templateId: string,
      phaseId: string,
      payload: PhaseTemplateSplitRequest,
    ) {
      const errors = usePhaseTemplateRulesStore().validateSplitPhaseRequest(
        templateId,
        phaseId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const splittedPhases = await TemplatesService.splitTemplatePhase(
        templateId,
        phaseId,
        payload,
      );
      await useTemplateStore().loadTemplate(templateId);

      return splittedPhases[0];
    },

    async distribute(templateId: string, phaseId: string) {
      await TemplatesService.distributeInPhase(templateId, phaseId);
      await useTemplateStore().loadTemplate(templateId);
    },

    async remove(templateId: string, phaseId: string) {
      const errors = usePhaseTemplateRulesStore().checkRemovePhase(
        templateId,
        phaseId,
      );
      if (errors) {
        throw errors;
      }
      await TemplatesService.deletePhase(templateId, phaseId);
      const templateStore = useTemplateStore();
      await templateStore.loadTemplate(templateId);
    },
  },
});
